import { all, call, fork, put, takeLatest } from '@redux-saga/core/effects';

import { apiServiceUrl, apiSuccessfulCode, defaultErrorMessage } from '../../constants';
import { downloadDataFromApiPromise, fetchGetDataObjectMethod, GetErrorMessageFromStatus } from '../../helpers/api';
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';
import { getLoggedInUser } from '../../helpers/authUtils';
import { addNotification } from '../../helpers/notification';
import * as Actions from './actions';
import * as ActionsType from './constants';

const reportApiServiceUrl = `${apiServiceUrl}/report`

const CLIENT_PDF_FAILED_MESSAGE = "We couldn't download the client PDF.";
const VP_PDF_FAILED_MESSAGE = "We couldn't download the value proposition PDF.";

/**
 * Fetch a PDF and hand it to the browser. Returns null on success, or a short
 * reason for the user on any failure (non-200, unreadable body, network).
 */
function* downloadPdf(url, fileName, fallback) {
    const user = getLoggedInUser();
    try {
        const response = yield call(fetchGetDataObjectMethod, url, user.token);

        if (response.status !== apiSuccessfulCode) {
            const failedBody = yield Promise.resolve()
                .then(() => response.text())
                .catch(() => null);
            return getApiErrorMessage({ status: response.status, body: failedBody }, fallback);
        }

        const { errorMessage } = yield downloadDataFromApiPromise(response, defaultErrorMessage, fileName)
            .then((result) => ({ errorMessage: result.errorMessage }))
            .catch(() => ({ errorMessage: defaultErrorMessage }));
        return errorMessage ? fallback : null;
    } catch (error) {
        return fallback;
    }
}

function showDownloadFailedToast(message) {
    addNotification({ title: 'Download failed', message, type: 'danger' });
}

export function* downloadClientReport({ payload: { clientId, clientName }}) {
    const failure = yield* downloadPdf(
        `${reportApiServiceUrl}/GetValuePropositionByClientReport?clientId=${clientId}`,
        `${clientName} Report.pdf`,
        CLIENT_PDF_FAILED_MESSAGE
    );
    if (failure) {
        showDownloadFailedToast(failure);
        yield put(Actions.downloadClientReportFailed(failure));
    } else {
        yield put(Actions.downloadClientReportSuccess());
    }
}

export function* downloadValuePropositionReport({ payload: { vpId, vpName }}) {
    const failure = yield* downloadPdf(
        `${reportApiServiceUrl}/GetValuePropositionReport?valuePropositionId=${vpId}`,
        `${vpName} Report.pdf`,
        VP_PDF_FAILED_MESSAGE
    );
    if (failure) {
        showDownloadFailedToast(failure);
        yield put(Actions.downloadValuePropositionReportFailed(failure));
    } else {
        yield put(Actions.downloadValuePropositionReportSuccess());
    }
}

function* downloadClientStatementListReport({ payload: { clientIds } = {} }) {
    const user = getLoggedInUser();
    const query =
        clientIds && Array.isArray(clientIds) && clientIds.length > 0
            ? `?clientIds=${clientIds.join(',')}`
            : '';
    try {
        const response = yield call(
            fetchGetDataObjectMethod,
            `${reportApiServiceUrl}/GetClientStatementListReport${query}`,
            user.token
        );

        if (response.status === apiSuccessfulCode) {
            const { errorMessage } = yield downloadDataFromApiPromise(
                response,
                defaultErrorMessage,
                'PITM-Client-Statement-List.pdf'
            )
                .then((res) => ({ errorMessage: res.errorMessage }))
                .catch(() => ({ errorMessage: defaultErrorMessage }));
            if (!errorMessage) {
                yield put(Actions.downloadClientStatementListReportSuccess());
            } else {
                yield put(Actions.downloadClientStatementListReportFailed(GetErrorMessageFromStatus(response)));
            }
        } else {
            yield put(Actions.downloadClientStatementListReportFailed(GetErrorMessageFromStatus(response)));
        }
    } catch (error) {
        yield put(Actions.downloadClientStatementListReportFailed(GetErrorMessageFromStatus(error)));
    }
}

export function* watchDownloadClientReport(): any {
    yield takeLatest(ActionsType.DOWNLOAD_CLIENT_REPORT, downloadClientReport);
}

export function* watchDownloadValuePropositionReport(): any {
    yield takeLatest(ActionsType.DOWNLOAD_VALUE_PROPOSITION_REPORT, downloadValuePropositionReport);
}

export function* watchDownloadClientStatementListReport(): any {
    yield takeLatest(ActionsType.DOWNLOAD_CLIENT_STATEMENT_LIST_REPORT, downloadClientStatementListReport);
}

function* reportSaga(): any {
    yield all([
        fork(watchDownloadClientReport),
        fork(watchDownloadValuePropositionReport),
        fork(watchDownloadClientStatementListReport),
    ]);
}

export default reportSaga;
